import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

type StepFocus =
  | "sidebar"
  | "sidebar_essentials"
  | "sidebar_calculators"
  | "projects"
  | "workspace";

type TutorialStep = {
  title: string;
  body: string;
  focus: StepFocus;
};

const steps: TutorialStep[] = [
  {
    title: "Control Panel",
    body:
      "Your centralized command center. Access core tools, calculators, and project data from one consistent location.",
    focus: "sidebar",
  },
  {
    title: "Essential Tools",
    body:
      "Document decisions, track progress, and capture critical project information in a structured environment. Information stored here is organized, searchable, and retained for long-term reference.",
    focus: "sidebar_essentials",
  },
  {
    title: "Calculators",
    body:
      "Build, standardize, and reuse engineering calculators across projects. Calculators stored in Calcera are secure, version-controlled, and protected from loss, ensuring firm knowledge remains with the company.",
    focus: "sidebar_calculators",
  },
  {
    title: "Projects",
    body:
      "All project-specific notes, tasks, milestones, and deliverables are centralized here. Project data is preserved, searchable, and protected, eliminating the risk of information loss when team members transition.",
    focus: "projects",
  },
  {
    title: "Workspace",
    body:
      "Open and interact with tools in a flexible workspace designed around engineering workflows. Organize your environment to match how you think and work.",
    focus: "workspace",
  },
];

export default function TutorialOverlay({
  onFinish,
}: {
  onFinish: () => void;
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  const [focusRect, setFocusRect] = useState<DOMRect | null>(null);

  // Measure focused element
  useEffect(() => {
    const updateFocus = () => {
      const el = document.querySelector(
        `[data-tutorial="${step.focus}"]`
      ) as HTMLElement | null;

      if (!el) {
        setFocusRect(null);
        return;
      }

      setFocusRect(el.getBoundingClientRect());
    };

    updateFocus();
    window.addEventListener("resize", updateFocus);

    return () => window.removeEventListener("resize", updateFocus);
  }, [step.focus]);

  // React to collapsible changes
  useEffect(() => {
    const el = document.querySelector(
      `[data-tutorial="${step.focus}"]`
    ) as HTMLElement | null;

    if (!el) return;

    const observer = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      if (step.focus === "workspace") {
        const sidebar = document.querySelector(
          `[data-tutorial="sidebar"]`
        ) as HTMLElement | null;

        const bottomBar = document.querySelector(
          `[data-tutorial="bottom_bar"]`
        ) as HTMLElement | null;

        const sidebarRect = sidebar?.getBoundingClientRect();
        const bottomRect = bottomBar?.getBoundingClientRect();

        const adjustedRect = {
          top: rect.top,
          left: sidebarRect ? sidebarRect.right : rect.left,
          width: sidebarRect
            ? rect.right - sidebarRect.right - 5
            : rect.width,
          height: bottomRect
            ? bottomRect.top - rect.top
            : rect.height,
        };

        setFocusRect(adjustedRect as DOMRect);
      } else {
        setFocusRect(rect);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [step.focus]);

  // Prevent scroll while active
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const isWorkspaceStep = step.focus === "workspace";

  return (
    <div className="fixed inset-0 z-[9999] text-white">

      {/* Dim everything but the glowing region */}
      {focusRect && (
        <>
          {/* Top strip */}
          <div
            className="fixed left-0 right-0 bg-black/60 pointer-events-auto"
            style={{
              top: 0,
              height: focusRect.top,
            }}
          />

          {/* Bottom strip */}
          <div
            className="fixed left-0 right-0 bg-black/60 pointer-events-auto"
            style={{
              top: focusRect.bottom,
              bottom: 0,
            }}
          />

          {/* Left strip */}
          <div
            className="fixed bg-black/60 pointer-events-auto"
            style={{
              top: focusRect.top,
              left: 0,
              width: focusRect.left,
              height: focusRect.height,
            }}
          />

          {/* Right strip */}
          <div
            className="fixed bg-black/60 pointer-events-auto"
            style={{
              top: focusRect.top,
              left: focusRect.right,
              right: 0,
              height: focusRect.height,
            }}
          />
        </>
      )}

      {/* Animated glow frame */}
      {focusRect && (
        <motion.div
          layout
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="
            fixed z-30 pointer-events-none
            rounded-xl
            ring-2 ring-purple-500
            shadow-[0_0_35px_rgba(168,85,247,0.9)]
          "
          style={{
            top: focusRect.top,
            left: focusRect.left,
            width: focusRect.width,
            height: focusRect.height,
          }}
        />
      )}

      {/* Tutorial Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={stepIndex}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="
            fixed z-40
            w-full max-w-sm
            rounded-2xl
            border border-purple-500/50
            bg-black/90
            p-5
            shadow-[0_0_30px_rgba(168,85,247,0.4)]
            backdrop-blur-md
            text-center
          "
          style={{
            top: focusRect
              ? focusRect.top + focusRect.height / 2 - 80 // 80 ≈ half card height
              : window.innerHeight / 2,

            left: focusRect
              ? step.focus === "workspace"
                ? focusRect.width / 2
                : focusRect.right + 32
              : window.innerWidth / 2,
          }}
        >
          {!isWorkspaceStep && (
            <div
              className="
                absolute left-[-6px] top-1/2 -translate-y-1/2
                h-4 w-4 rotate-45
                bg-black/90
                border-l border-b border-purple-500/50
              "
            />
          )}

          <h3 className="mb-2 text-lg font-semibold">{step.title}</h3>
          <p className="mb-4 text-sm text-white/80">{step.body}</p>

          <Button
            size="sm"
            onClick={() =>
              isLast ? onFinish() : setStepIndex((i) => i + 1)
            }
            className="bg-purple-600 hover:bg-purple-500"
          >
            {isLast ? "Finish" : "Next"}
          </Button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}